import { useCallback, useEffect, useState, useContext } from "react";
import ProfileNavigation from "../../../components/common/ProfileNavigation";
import SEO from "../../../components/SEO";
import useUser, { getAuthorized, isUserClient, isUserDoctor } from "../../../hooks/User";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { ROUTES } from "../../../routes";
import { convertToUserTimezone, formatTimeInUserTimezone, formatDateInUserTimezone } from "../../../utils/timezone";
import {
    getSurveyOutboxCount,
    putRemindersListCache,
    getRemindersListCache,
} from "../../../utils/surveyOfflineDb";
import { prefetchSurveysForOffline } from "../../../utils/prefetchSurveysForOffline";
import { isLikelyOfflineOrNetworkError } from "../../../utils/offlineError";

import "./patientsurveys.css";

// Icons
const CalendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
);

const FileTextIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
        <polyline points="14 2 14 8 20 8"/>
    </svg>
);

const ClockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
    </svg>
);

const PlayIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
);

const CheckCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
);

const BellIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
    </svg>
);

const HistoryIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v5h5"/>
        <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/>
        <path d="M12 7v5l4 2"/>
    </svg>
);

const ChevronRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m9 18 6-6-6-6"/>
    </svg>
);

const FilterIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
    </svg>
);

// Status configs for reminders
const ReminderStatusConfig = {
    pending: { label: "Ожидает", className: "status-pending", icon: "⏳" },
    sent: { label: "Готов к прохождению", className: "status-sent", icon: "🔔" },
    completed: { label: "Завершён", className: "status-completed", icon: "✓" }
};

// Status configs for attempts
const AttemptStatusConfig = {
    completed: { 
        label: "Завершён", 
        className: "status-completed",
        icon: <CheckCircleIcon />
    },
    in_progress: { 
        label: "В процессе", 
        className: "status-in-progress",
        icon: <ClockIcon />
    },
    draft: { 
        label: "Черновик", 
        className: "status-draft",
        icon: <FileTextIcon />
    },
    overdue: { 
        label: "Просрочен", 
        className: "status-overdue",
        icon: <ClockIcon />
    }
};

// Tab Switcher Component
function TabSwitcher({ activeTab, onTabChange }) {
    return (
        <div className="surveys-tab-switcher">
            <button
                className={`tab-btn ${activeTab === 'current' ? 'active' : ''}`}
                onClick={() => onTabChange('current')}
            >
                <BellIcon />
                <span>Текущие</span>
            </button>
            <button
                className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => onTabChange('history')}
            >
                <HistoryIcon />
                <span>История</span>
            </button>
        </div>
    );
}

// Reminder Card Component
function ReminderCard({ reminder, onTakeSurvey }) {
    const status = ReminderStatusConfig[reminder.status] || ReminderStatusConfig.pending;
    const isAvailable = reminder.status === "sent";
    const isCompleted = reminder.status === "completed";

    return (
        <div className={`reminder-card ${isCompleted ? 'completed' : ''}`}>
            <div className="reminder-header">
                <div className="reminder-status-badge" data-status={reminder.status}>
                    <span className="status-icon">{status.icon}</span>
                    <span>{status.label}</span>
                </div>
                <div className="reminder-time">
                    <ClockIcon />
                    <span>{formatTimeInUserTimezone(reminder.scheduled_time, "HH:mm")}</span>
                </div>
            </div>

            <div className="reminder-content">
                <h3 className="reminder-title">
                    <FileTextIcon />
                    {reminder.scheduled_survey_title || reminder.survey_title}
                </h3>
                <p className="reminder-survey-name">{reminder.survey_title}</p>
                {reminder.survey_description && (
                    <p className="reminder-description">{reminder.survey_description}</p>
                )}
            </div>

            <div className="reminder-footer">
                <span className="reminder-date">
                    {formatDateInUserTimezone(reminder.scheduled_time, "DD.MM.YYYY")}
                </span>
                <button 
                    className="btn-take-survey"
                    onClick={() => onTakeSurvey(reminder)}
                    disabled={!isAvailable}
                >
                    {isAvailable ? (
                        <>
                            <PlayIcon />
                            <span>Пройти</span>
                        </>
                    ) : isCompleted ? (
                        <>
                            <CheckCircleIcon />
                            <span>Пройден</span>
                        </>
                    ) : (
                        <span>Ожидается</span>
                    )}
                </button>
            </div>
        </div>
    );
}

// Attempt Card Component
function AttemptCard({ attempt, onViewDetails }) {
    const status = AttemptStatusConfig[attempt.status] || AttemptStatusConfig.draft;
    const progress = attempt.total_questions_count > 0 
        ? Math.round((attempt.answered_questions_count / attempt.total_questions_count) * 100)
        : 0;

    return (
        <div className="attempt-card" onClick={() => onViewDetails(attempt)}>
            <div className="attempt-card-header">
                <div className={`attempt-status-badge ${status.className}`}>
                    {status.icon}
                    <span>{status.label}</span>
                </div>
                <span className="attempt-date">
                    {dayjs(attempt.creation_dt).format("DD.MM.YYYY HH:mm")}
                </span>
            </div>

            <div className="attempt-card-content">
                <h3 className="attempt-survey-title">
                    <FileTextIcon />
                    {attempt.survey_title}
                </h3>
                {attempt.survey_description && (
                    <p className="attempt-survey-description">{attempt.survey_description}</p>
                )}
            </div>

            <div className="attempt-card-footer">
                <div className="attempt-progress">
                    <div className="progress-bar">
                        <div 
                            className="progress-fill" 
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className="progress-text">
                        {attempt.answered_questions_count} / {attempt.total_questions_count} вопросов
                    </span>
                </div>
                <button className="btn-view-details">
                    Подробнее
                    <ChevronRightIcon />
                </button>
            </div>
        </div>
    );
}

// Date Selector Component
function DateSelector({ date, onChange }) {
    return (
        <div className="date-selector">
            <CalendarIcon />
            <input 
                type="date" 
                value={date} 
                onChange={(e) => onChange(e.target.value)}
                className="date-input"
            />
        </div>
    );
}

// Attempt Filters Component
function AttemptFilters({ status, onStatusChange }) {
    const filters = [
        { value: "", label: "Все статусы" },
        { value: "completed", label: "Завершённые" },
        { value: "in_progress", label: "В процессе" },
        { value: "draft", label: "Черновики" },
        { value: "overdue", label: "Просроченные" }
    ];

    return (
        <div className="attempt-filters">
            <FilterIcon />
            <select 
                value={status} 
                onChange={(e) => onStatusChange(e.target.value)}
                className="filter-select"
            >
                {filters.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                ))}
            </select>
        </div>
    );
}

// Current Surveys View
function CurrentSurveysView({ reminders, loading, date, onTakeSurvey, onDateChange }) {
    const today = dayjs().format("YYYY-MM-DD");
    const isToday = date === today;

    // Group reminders by status
    const availableSurveys = reminders.filter(r => r.status === "sent");
    const pendingSurveys = reminders.filter(r => r.status === "pending");
    const completedSurveys = reminders.filter(r => r.status === "completed");

    return (
        <>
            <div className="surveys-header">
                <div className="surveys-title">
                    <BellIcon />
                    <div>
                        <h1>Назначенные опросы</h1>
                        <p className="surveys-subtitle">
                            {isToday ? "Сегодня" : dayjs(date).format("DD.MM.YYYY")}
                            {availableSurveys.length > 0 && (
                                <span className="available-count"> • {availableSurveys.length} доступно</span>
                            )}
                        </p>
                    </div>
                </div>
                <DateSelector date={date} onChange={onDateChange} />
            </div>

            {loading ? (
                <div className="surveys-loading">
                    <div className="spinner"></div>
                    <p>Загрузка опросов...</p>
                </div>
            ) : reminders.length === 0 ? (
                <div className="empty-surveys-state">
                    <div className="empty-icon">📋</div>
                    <h3>Нет запланированных опросов</h3>
                    <p>На выбранную дату нет назначенных опросов</p>
                </div>
            ) : (
                <div className="surveys-content">
                    {/* Available Surveys Section */}
                    {availableSurveys.length > 0 && (
                        <div className="surveys-section">
                            <h2 className="section-title">
                                <span className="section-dot available"></span>
                                Готовы к прохождению ({availableSurveys.length})
                            </h2>
                            <div className="reminders-grid">
                                {availableSurveys.map(reminder => (
                                    <ReminderCard 
                                        key={reminder.id}
                                        reminder={reminder}
                                        onTakeSurvey={onTakeSurvey}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Pending Surveys Section */}
                    {pendingSurveys.length > 0 && (
                        <div className="surveys-section">
                            <h2 className="section-title">
                                <span className="section-dot pending"></span>
                                Ожидают ({pendingSurveys.length})
                            </h2>
                            <div className="reminders-grid">
                                {pendingSurveys.map(reminder => (
                                    <ReminderCard 
                                        key={reminder.id}
                                        reminder={reminder}
                                        onTakeSurvey={onTakeSurvey}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Completed Surveys Section */}
                    {completedSurveys.length > 0 && (
                        <div className="surveys-section">
                            <h2 className="section-title">
                                <span className="section-dot completed"></span>
                                Пройдены ({completedSurveys.length})
                            </h2>
                            <div className="reminders-grid">
                                {completedSurveys.map(reminder => (
                                    <ReminderCard 
                                        key={reminder.id}
                                        reminder={reminder}
                                        onTakeSurvey={onTakeSurvey}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

// History View
function HistoryView({ attempts, loading, statusFilter, onStatusChange, onViewDetails, onLoadMore, hasMore }) {
    return (
        <>
            <div className="surveys-header">
                <div className="surveys-title">
                    <HistoryIcon />
                    <div>
                        <h1>История опросов</h1>
                        <p className="surveys-subtitle">
                            Все ваши попытки прохождения опросов
                        </p>
                    </div>
                </div>
                <AttemptFilters 
                    status={statusFilter} 
                    onStatusChange={onStatusChange}
                />
            </div>

            {loading && attempts.length === 0 ? (
                <div className="surveys-loading">
                    <div className="spinner"></div>
                    <p>Загрузка истории...</p>
                </div>
            ) : attempts.length === 0 ? (
                <div className="empty-surveys-state">
                    <div className="empty-icon">
                        <HistoryIcon />
                    </div>
                    <h3>История пуста</h3>
                    <p>
                        {statusFilter 
                            ? "Нет опросов с выбранным статусом"
                            : "Вы ещё не проходили ни одного опроса"
                        }
                    </p>
                </div>
            ) : (
                <div className="surveys-content">
                    <div className="attempts-grid">
                        {attempts.map(attempt => (
                            <AttemptCard 
                                key={attempt.attempt_id}
                                attempt={attempt}
                                onViewDetails={onViewDetails}
                            />
                        ))}
                    </div>
                    
                    {hasMore && (
                        <div className="load-more-container">
                            <button 
                                className="btn-load-more"
                                onClick={onLoadMore}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <div className="spinner-small"></div>
                                        Загрузка...
                                    </>
                                ) : (
                                    "Загрузить ещё"
                                )}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

// Main Component
function PatientSurveys() {
    const navigate = useNavigate();
    const { setErrorList, addNotification } = useContext(ErrorHandlerContext);
    
    // Tab state
    const [activeTab, setActiveTab] = useState('current');
    
    // Current surveys state
    const [reminders, setReminders] = useState([]);
    const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
    const [loadingReminders, setLoadingReminders] = useState(true);
    const [outboxPending, setOutboxPending] = useState(0);
    
    // History state
    const [attempts, setAttempts] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const PAGE_SIZE = 10;

    // Load reminders
    const loadReminders = useCallback(async () => {
        setLoadingReminders(true);
        try {
            const response = await getAuthorized("/client/my_reminders", {
                params: { 
                    for_date: date,
                    status: "sent"
                }
            });
            
            const sorted = (response.data?.reminders || []).sort((a, b) => {
                const timeA = convertToUserTimezone(a.scheduled_time);
                const timeB = convertToUserTimezone(b.scheduled_time);
                return timeA.diff(timeB);
            });
            
            setReminders(sorted);
            void putRemindersListCache(date, sorted);
            void prefetchSurveysForOffline(sorted, getAuthorized);
        } catch (error) {
            console.error("Failed to load reminders:", error);
            if (isLikelyOfflineOrNetworkError(error)) {
                const cached = await getRemindersListCache(date);
                if (cached?.length) {
                    setReminders(cached);
                    addNotification({
                        msg: "Нет сети. Показан последний загруженный список опросов за эту дату.",
                        type: "success",
                        duration: 6000,
                    });
                } else {
                    setErrorList(prev => [...prev, "Нет сети и нет сохранённого списка за эту дату. Подключитесь и откройте страницу ещё раз."]);
                }
            } else {
                setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при загрузке напоминаний"]);
            }
        } finally {
            setLoadingReminders(false);
            getSurveyOutboxCount().then(setOutboxPending).catch(() => {});
        }
    }, [date, setErrorList, addNotification]);

    useEffect(() => {
        const refreshOutbox = () => {
            getSurveyOutboxCount().then(setOutboxPending).catch(() => {});
        };
        const onVis = () => {
            if (document.visibilityState === "visible") refreshOutbox();
        };
        window.addEventListener("online", refreshOutbox);
        document.addEventListener("visibilitychange", onVis);
        refreshOutbox();
        return () => {
            window.removeEventListener("online", refreshOutbox);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, []);

    useEffect(() => {
        const onFlushed = (e) => {
            const n = e.detail?.count ?? 0;
            if (n > 0) {
                addNotification({
                    msg: n === 1 ? "Опрос отправлен на сервер." : `Отправлено ответов: ${n}.`,
                    type: "success",
                    duration: 6000,
                });
            }
            getSurveyOutboxCount().then(setOutboxPending).catch(() => {});
        };
        window.addEventListener("spiria-survey-outbox-flushed", onFlushed);
        return () => window.removeEventListener("spiria-survey-outbox-flushed", onFlushed);
    }, [addNotification]);

    // Load attempts
    const loadAttempts = useCallback(async (reset = false) => {
        if (reset) {
            setPage(0);
            setAttempts([]);
        }
        
        setLoadingHistory(true);
        try {
            const params = {
                st: reset ? 0 : page * PAGE_SIZE,
                fn: (reset ? 0 : page * PAGE_SIZE) + PAGE_SIZE - 1,
            };
            
            if (statusFilter) {
                params.status = statusFilter;
            }

            const response = await getAuthorized("/client/my_attempts", { params });
            
            const newAttempts = response.data?.attempts || [];
            const totalCount = response.data?.total_count || 0;
            
            if (reset) {
                setAttempts(newAttempts);
            } else {
                setAttempts(prev => [...prev, ...newAttempts]);
            }
            
            const currentTotal = reset ? newAttempts.length : attempts.length + newAttempts.length;
            setHasMore(currentTotal < totalCount);
            
            if (!reset) {
                setPage(prev => prev + 1);
            }
        } catch (error) {
            console.error("Failed to load attempts:", error);
            if (!isLikelyOfflineOrNetworkError(error)) {
                setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при загрузке истории"]);
            }
        } finally {
            setLoadingHistory(false);
        }
    }, [statusFilter, page, attempts.length, setErrorList]);

    // Load data when tab changes
    useEffect(() => {
        if (activeTab === 'current') {
            loadReminders();
        } else if (activeTab === 'history') {
            loadAttempts(true);
        }
    }, [activeTab]);

    // Load reminders when date changes
    useEffect(() => {
        if (activeTab === 'current') {
            loadReminders();
        }
    }, [date, activeTab]);

    // Load attempts when filter changes
    useEffect(() => {
        if (activeTab === 'history') {
            loadAttempts(true);
        }
    }, [statusFilter, activeTab]);

    const handleTakeSurvey = useCallback((reminder) => {
        const qs = new URLSearchParams({
            surveyId: String(reminder.survey_id),
            reminderId: String(reminder.id),
        });
        navigate(`${ROUTES.TAKE_SURVEY}?${qs.toString()}`, {
            viewTransition: true,
            state: {
                surveyId: reminder.survey_id,
                reminderId: reminder.id,
            },
        });
    }, [navigate]);

    const handleViewDetails = useCallback((attempt) => {
        navigate(ROUTES.SURVEY_ATTEMPT_DETAIL, {
            viewTransition: true,
            state: { attemptId: attempt.attempt_id }
        });
    }, [navigate]);

    const handleLoadMore = () => {
        loadAttempts(false);
    };

    return (
        <div className="patient-surveys-container">
            {outboxPending > 0 && (
                <div className="survey-outbox-banner" role="status">
                    В очереди на отправку: {outboxPending}. При появлении интернета ответы отправятся автоматически (в том числе после переключения вкладки). Вы увидите уведомление об успешной отправке.
                </div>
            )}
            <TabSwitcher activeTab={activeTab} onTabChange={setActiveTab} />
            
            {activeTab === 'current' ? (
                <CurrentSurveysView
                    reminders={reminders}
                    loading={loadingReminders}
                    date={date}
                    onTakeSurvey={handleTakeSurvey}
                    onDateChange={setDate}
                />
            ) : (
                <HistoryView
                    attempts={attempts}
                    loading={loadingHistory}
                    statusFilter={statusFilter}
                    onStatusChange={setStatusFilter}
                    onViewDetails={handleViewDetails}
                    onLoadMore={handleLoadMore}
                    hasMore={hasMore}
                />
            )}
        </div>
    );
}

// Page Component with Error Handler
function PatientSurveysPage() {
    const { data, loading, error } = useUser();
    const navigate = useNavigate();

    useEffect(() => {
        if (loading) return;
        if (error) navigate(ROUTES.AUTH, { viewTransition: true });
        // Разрешаем доступ всем пользователям с ролью client (включая врачей)
        if (data && !isUserClient(data)) navigate(ROUTES.PROFILE, { viewTransition: true });
    }, [data, error, loading, navigate]);

    if (loading) {
        return (
            <>
                <ProfileNavigation isDoctor={false} />
                <div className="patient-surveys-page">
                    <div className="surveys-loading">
                        <div className="spinner"></div>
                        <p>Загрузка...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO title="Мои опросы" description="Список доступных медицинских опросов для прохождения" noindex={true} />
            <ErrorHandlerContextProvider>
                <ProfileNavigation isDoctor={isUserDoctor(data)} />
                <div className="patient-surveys-page">
                    <PatientSurveys />
                </div>
                <ErrorHandler />
            </ErrorHandlerContextProvider>
        </>
    );
}

export default PatientSurveysPage;
